import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { AvailabilityCalendar, AvailabilityCalendarDatePicker } from "mueller-controls";
import MCard from "components/Shared/MCard.tsx";
import moment from "moment";
import { getAppraiserListByAppraisalId } from "actions/appraiser";
import { getCalendarEventsByEmployeeId } from "requests/employeeCalendarEvent";
import { AppraisalWorkflowStatusCode } from "typeConstants/appraisalWorkflowStatusCodes";
import { getHolidaysBetweenDates } from "requests/holiday/index.ts";
import { getHolidayMouseoverText } from "components/Employee/EmployeeCalendar.tsx";
import { createRequestCoalescer } from "tools/requestCoalescer";

const appraiserHolidayCoalescer = createRequestCoalescer();
const employeeCalendarEventsCoalescer = createRequestCoalescer();
const activeRequest = { current: true };

const AppraiserAvailabilityView = props => {
  const [calendarEntries, setCalendarEntries] = useState([]);
  const [monthsLoaded, setMonthsLoaded] = useState([]);
  const [currentDate, setCurrentDate] = useState(moment());
  const mounted = useRef(true);
  const holidayRequestCleanup = useRef(() => {});
  const calendarRequestCleanups = useRef([]);
  const [holidays, setHolidays] = useState();

  useEffect(() => {
    const holidayLoadRangeYears = 2;
    const holidayStart = moment().subtract(holidayLoadRangeYears, "year").startOf("day").toDate();
    const holidayEnd = moment().add(holidayLoadRangeYears, "year").endOf("day").toDate();
    const holidayKey = `${holidayStart.toISOString()}|${holidayEnd.toISOString()}`;
    holidayRequestCleanup.current = appraiserHolidayCoalescer.run(
      holidayKey,
      (resolve, reject) => getHolidaysBetweenDates(holidayStart, holidayEnd, resolve, activeRequest, reject),
      data => {
        if (mounted.current) {
          setHolidays(data);
        }
      },
    );
    return () => {
      mounted.current = false;
      holidayRequestCleanup.current();
      calendarRequestCleanups.current.forEach(cleanup => cleanup());
      calendarRequestCleanups.current = [];
    };
  }, []);

  useEffect(() => {
    props.getAppraiserListByAppraisalId(props.controlIdNumber);
  }, [props.controlIdNumber]);

  useEffect(() => {
    loadAllEntriesForAppraisers(currentDate.clone().startOf("month"), currentDate.clone().endOf("month"));
  }, [props.assignableAppraisers]);

  const getMonthNumber = momentDate => parseInt(momentDate.format("YYYYMM"));
  const maxMonth = getMonthNumber(moment());

  const onDateChanged = date => {
    let startOfMonth = date.clone().startOf("month");
    let endOfMonth = date.clone().endOf("month");
    let monthNum = getMonthNumber(startOfMonth);
    setCurrentDate(date);

    if (monthNum < maxMonth && !monthsLoaded.some(m => m === monthNum)) {
      setMonthsLoaded([...monthsLoaded, monthNum]);
      loadAllEntriesForAppraisers(startOfMonth, endOfMonth);
    }
  };

  const loadAllEntriesForAppraisers = (startDate, endDate) => {
    calendarRequestCleanups.current.forEach(cleanup => cleanup());
    calendarRequestCleanups.current = [];

    let availableAppraisers = props.assignableAppraisers?.appraisersAvailable ?? [];
    let inactiveAppraisesrs = props.assignableAppraisers?.appraisersIneligible ?? [];
    let allAppraisers = [...availableAppraisers, ...inactiveAppraisesrs];
    allAppraisers?.forEach(a => {
      const requestKey = `${a.employeeId}|${startDate.toISOString()}|${endDate.toISOString()}`;

      const unsubscribe = employeeCalendarEventsCoalescer.run(
        requestKey,
        (resolve, reject) => getCalendarEventsByEmployeeId(a.employeeId, startDate, endDate, resolve, reject, activeRequest),
        entries => {
          if (mounted.current) {
            combineEntries(entries);
          }
        },
      );

      calendarRequestCleanups.current.push(unsubscribe);
    });
  };

  // set the new entries in state, combine with any old entries that we didnt get fresh data for
  const combineEntries = serverEntries => {
    setCalendarEntries(previousEntries => [
      ...serverEntries,
      ...previousEntries.filter(e => !serverEntries.some(se => se.timeType === e.timeType && se.id === e.id)),
    ]);
  };

  const mapAppraisersForCalendar = () => {
    let activeAppraisers = props.assignableAppraisers?.appraisersAvailable?.map(a => ({
      name: (a.isSalary ? "S-" : "") + a.fullName + ` (${a.performanceGrade}; ${a.proximityToSubject} mi)`,
      employeeId: a.employeeId,
    })) ?? [];
    let inactiveAppraisers = props.assignableAppraisers?.appraisersIneligible?.map(a => ({
      name: (a.isSalary ? "S-" : "") + a.fullName + ` (${a.performanceGrade}; ${a.proximityToSubject} mi)`,
      employeeId: a.employeeId,
      className: "ineligible",
    })) ?? [];
    return [...activeAppraisers, ...inactiveAppraisers];
  };
  
  return (
    <MCard title="Appraiser List" className="employee-availability-calendar">
      <AvailabilityCalendarDatePicker date={currentDate} onDateChanged={onDateChanged} />
      <AvailabilityCalendar 
        date={currentDate} 
        employees={mapAppraisersForCalendar() ?? []} 
        calendarEntries={calendarEntries}
        startTime={0}
        endTime={24}
        scrollStartTime={8}
        holidays={holidays}
        getHolidayMouseoverText={getHolidayMouseoverText}
      />
    </MCard>
  );
};

AppraiserAvailabilityView.propTypes = {
  controlIdNumber: PropTypes.number,
  // Redux
  assignableAppraisers: PropTypes.object,
  getAppraiserListByAppraisalId: PropTypes.func,
};

const mapStateToProps = (state) => {
  return {
      assignableAppraisers: state.caseDetails.assignableAppraisers,
  };
};

const mapDispatchToProps = (dispatch) => ({
  getAppraiserListByAppraisalId(controlIdNumber) {
      dispatch(getAppraiserListByAppraisalId(controlIdNumber));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(AppraiserAvailabilityView);

export const AppraiserAvailabilityViewStatusCodes = [
  AppraisalWorkflowStatusCode.AVAILABLE_TO_APPRAISER,
  AppraisalWorkflowStatusCode.REJECTED_TO_APPRAISER,
  AppraisalWorkflowStatusCode.CUSTOMER_REJECTED,
  AppraisalWorkflowStatusCode.APPRAISER_RECONSIDERING,
  AppraisalWorkflowStatusCode.VEROS_PREVIEW_RESULT_ERROR,
  AppraisalWorkflowStatusCode.VEROS_PREVIEW_ERROR_APPRAISER,
  AppraisalWorkflowStatusCode.UCDP_RESULT_RECEIVED_WITH_ERRORS,
  AppraisalWorkflowStatusCode.AWAITING_FIELD_REPORT,
  AppraisalWorkflowStatusCode.APPRAISAL_SUBMITTAL_VERIFICATION,
  AppraisalWorkflowStatusCode.VEROS_PREVIEW_REQUESTED,
  AppraisalWorkflowStatusCode.GAAR_PREVIEW_REQUESTED,
  AppraisalWorkflowStatusCode.UCDP_SUBMITTED_PENDING_RESPONSE,
  AppraisalWorkflowStatusCode.UCDP_RESULT_RECEIVED_NO_ERRORS,
  AppraisalWorkflowStatusCode.AWAITING_ELIGIBILITY_DECISION,
  AppraisalWorkflowStatusCode.PENDING_APPRAISAL_ASSIGNMENT,
  AppraisalWorkflowStatusCode.REJECTED_TO_FIELD,
  AppraisalWorkflowStatusCode.UNASSIGNED,
];